import { ZoneInfo } from "Services/API/API";
import moment from "moment";
import { AnyDateTime, DateOnly, TimeOnly } from "typings/DateTime";

export const HOURS_0_VALUE = "00:00:00";
/** 
 * Note that sometimes times are compared lexicographically.
 * So "1.00:00:00" < "10:00:00" 
 * See getNewEndTime() */
export const HOURS_24_VALUE = "24:00:00";

const SERVER_UNSPESIFIED_FORMAT = "yyyy-MM-DDTHH:mm:ss";

/*
This method was introduced along side eslinting rule "no-date-parsing"
We do not want to use new Date("") or Date.parse("") because they are inconsistent.
Specifically, passing a DateOnly argument (ex. "2024-03-15") will create a date assuming the param is in UTC
(so for US/Central actual Date would be "Thu Mar 14 2024 20:00:00 GMT-0400 (Eastern Daylight Time)"),
while moment("") will populate the date assuming it is local (expected behaviour).
*/
export function toDate(date: string | DateOnly | Date, format?: string, locale?: string): Date
export function toDate(date: string | DateOnly | Date | undefined | null, format?: string, locale?: string): Date | undefined | null
export function toDate(date: string | DateOnly | Date | undefined | null, format?: string, locale?: string): Date | undefined | null {
   if (date == undefined)
      return date;

   //Apply default format for DateOnly dates
   if (!format && !(date instanceof Date) && isDateOnly(date))
      format = moment.HTML5_FMT.DATE;

   return moment(date, format, locale).toDate();
}

export function toLocalDate(date: string): Date
export function toLocalDate(date: string | undefined | null): Date | undefined | null {
   if (date == undefined)
      return date;
   const fixedDate = date.replace("Z", "");
   return toDate(fixedDate);
}

export const getMilliseconds = (date?: string) => {
   if (!date) return Date.now();
   return toDate(date).getTime();
};

export const isToday = (date?: string) => {
   if (!date) return false;
   return moment(date).isSame(new Date(), "day");
};

export const isFirstDayOfMonth = (date?: string) => {
   if (!date) return false;
   return moment(date).date() === 1;
};

export const getStartOfDay = (date?: string) => {
   return moment(date).startOf("day").toISOString();
};

export const getEndOfDay = (date?: string) => {
   return moment(date).endOf("day").toISOString();
};

export const getStartOfWeek = (date?: string) => {
   return moment(date).startOf("week").toISOString();
};

export const getEndOfWeek = (date?: string) => {
   return moment(date).endOf("week").toISOString();
};

export const getStartOfMonth = (date?: string | Date) => {
   return moment(date).startOf("month").toISOString();
};

export const getStartOfNextMonth = (date?: string | Date) => {
   return moment(date).add(1, "month").startOf("month").toISOString();
};

export const getEndOfMonth = (date?: string) => {
   return moment(date).endOf("month").toISOString();
};

export const addTimezoneOffset = (date?: string) => {
   const d = date ? toDate(date) : new Date();
   const userTimezoneOffset = d.getTimezoneOffset() * 60000;
   d.setMilliseconds(d.getMilliseconds() + userTimezoneOffset);
   return d.toISOString();
};

//TODO: REPLACE with toISOStringUnspecified usage or similar
export const subTimezoneOffset = (date?: string) => {
   const d = date ? toDate(date) : new Date();
   const userTimezoneOffset = d.getTimezoneOffset() * 60000;
   d.setMilliseconds(d.getMilliseconds() - userTimezoneOffset);
   return d.toISOString();
};

//Overloads to reduce undefined checks when date is defined
export function toISOStringUnspecified(date: string | Date | moment.Moment): string;
export function toISOStringUnspecified(date: string | Date | moment.Moment | undefined): string | undefined;
export function toISOStringUnspecified(date: string | Date | moment.Moment | undefined) {
   if (!date) return date;

   if (typeof date == "string" || date instanceof Date)
      return moment(date).format(SERVER_UNSPESIFIED_FORMAT);

   return date.format(SERVER_UNSPESIFIED_FORMAT);
}

type Units =
   | "milliseconds"
   | "seconds"
   | "minutes"
   | "days"
   | "months"
   | "years";

export const dateSubtract = (
   date: string,
   count: number,
   unit: Units = "milliseconds"
) => {
   const newMomentDate = moment(date).subtract(count, unit);
   return newMomentDate.toISOString();
};

export function isDateOnly(date: string): date is DateOnly {
   const regex = /^[0-9]{4}[-][0-9]{2}[-][0-9]{2}$/g;
   return regex.test(date);
}
export function toDateOnly(date: string | Date, useUtc?: boolean): DateOnly
export function toDateOnly(date: string | Date | undefined, useUtc?: boolean): DateOnly | undefined
export function toDateOnly(date: string | Date | undefined, useUtc?: boolean): DateOnly | undefined {
   if (!date)
      return undefined;
   return splitDateAndTime(date, useUtc)[0];
}

export function isTimeOnly(date: AnyDateTime): date is TimeOnly {
   if (typeof date === "string") {
      const regex = /^(1.)?[0-9]{2}[:][0-9]{2}[:][0-9]{2}$/g;
      return regex.test(date);
   }
   else return false;
}
export function toTimeOnly(date: string | Date): TimeOnly
export function toTimeOnly(date: string | Date | undefined): TimeOnly | undefined
export function toTimeOnly(date: string | Date | undefined): TimeOnly | undefined {
   if (!date)
      return undefined;
   return splitDateAndTime(date)[1];
}

export const splitDateAndTime = (date: string | Date, useUtc?: boolean): [DateOnly, TimeOnly] => {
   const dateMoment = moment(date);
   if (useUtc)
      dateMoment.utc();
   return [dateMoment.format(moment.HTML5_FMT.DATE), dateMoment.format(moment.HTML5_FMT.TIME_SECONDS)];
}

export const joinDateAndTime = (dateOnly: DateOnly, timeOnly?: TimeOnly): string => {
   const dateMoment = moment(dateOnly);
   if (timeOnly) {
      const timeMoment = moment(timeOnly, moment.HTML5_FMT.TIME_SECONDS);
      dateMoment.set("hour", timeMoment.hour());
      dateMoment.set("minute", timeMoment.minute());
      dateMoment.set("second", timeMoment.second());
   }
   return toISOStringUnspecified(dateMoment);
}

export const joinDateAndTimeUTC = (dateOnly: DateOnly, timeOnly?: TimeOnly): string => {
   const dateMoment = moment(dateOnly);
   dateMoment.utc(true);
   if (timeOnly) {
      const timeMoment = moment(timeOnly, moment.HTML5_FMT.TIME_SECONDS);
      dateMoment.set("hour", timeMoment.hour());
      dateMoment.set("minute", timeMoment.minute());
      dateMoment.set("second", timeMoment.second());
   }
   return dateMoment.toISOString();
}


//Adjusts local(browser) date to time zone date (still in local(browser) timezone)
export const toTimeZoneInfo = (date: Date, timeZoneInfo: ZoneInfo) => {
   if (!timeZoneInfo.tzdbIds.length) {
      console.log(`No time zone tzdbIds for timeZone ${timeZoneInfo.windowsId}`, timeZoneInfo);
      return date;
   }
   return toTimeZone(date, timeZoneInfo.tzdbIds[0]);
}

//Adjusts local(browser) date to time zone date (still in local(browser) timezone)
export const toTimeZone = (date: Date, timeZone: string) => {
   const locale = "en-US";
   return toDate(date.toLocaleString(locale, {
      timeZone: timeZone,
      hour12: false
   }), "MM/DD/YYYY, HH:mm:ss", locale);
}